import { useEffect, useState, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  XMarkIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline'
import DailyIframe from '@daily-co/daily-js'
import { isNativePlatform } from '../utils/platform'

export default function VideoCallPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const roomUrl = searchParams.get('roomUrl') || ''
  const userName = searchParams.get('userName') || 'User'
  const professionalName = searchParams.get('professionalName') || 'Professional'
  const isHost = searchParams.get('isHost') === 'true'

  const [callObject, setCallObject] = useState<any>(null)
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null)
  const creatingRef = useRef(false)

  // Create Daily frame when container is available
  useEffect(() => {
    if (containerElement && !callObject && !creatingRef.current) {
      creatingRef.current = true

      try {
        // Check if there's an existing Daily instance and destroy it first
        const existingInstances = DailyIframe.getCallInstance()
        if (existingInstances) {
          existingInstances.destroy()
        }

        const daily = DailyIframe.createFrame(containerElement, {
          showLeaveButton: true,
          showFullscreenButton: true,
          iframeStyle: {
            width: '100%',
            height: '100%',
            border: 'none'
          }
        })
        setCallObject(daily)
      } catch (error) {
        console.error('Error creating Daily frame:', error)
        creatingRef.current = false
      }
    }
  }, [containerElement, callObject])

  // Join meeting when call object is ready
  useEffect(() => {
    if (callObject && roomUrl) {
      callObject.join({
        url: roomUrl,
        userName: userName
      }).then(() => {
      }).catch((error: any) => {
        console.error('Failed to join meeting:', error)
      })
    }
  }, [callObject, roomUrl, userName])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (callObject) {
        callObject.destroy()
      }
    }
  }, [callObject])

  const handleClose = () => {
    // Clean up call object before navigating
    if (callObject) {
      callObject.destroy()
    }

    // On native platforms, navigate back to app
    // On web, close the window (if opened by window.open) or navigate back
    if (isNativePlatform()) {
      navigate('/app?view=meetings')
    } else {
      // Try to close window (only works if opened by window.open)
      // If that fails, navigate back to app
      try {
        window.close()
      } catch (e) {
        navigate('/app?view=meetings')
      }
    }
  }

  return (
    <div className="h-screen w-full bg-gray-900 flex flex-col">
      {/* Header - Extra top padding for iOS notch/safe area */}
      <div className="flex items-center justify-between px-4 py-4 bg-gray-800 border-b border-gray-700 flex-shrink-0" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
        <div className="flex items-center">
          <VideoCameraIcon className="h-6 w-6 mr-2 text-indigo-400" />
          <h1 className="text-xl font-semibold text-white">
            Video Consultation with {professionalName}
          </h1>
          {isHost && (
            <span className="ml-2 inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
              Host
            </span>
          )}
        </div>

        <button
          onClick={handleClose}
          className="rounded-md text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Video Call Content - Daily.co prebuilt UI */}
      <div className="flex-1">
        <div ref={setContainerElement} className="w-full h-full" />
      </div>
    </div>
  )
}
